import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getTasks,
  createTask,
  getStudents,
} from "../../services/admin.services";

// Async Thunks for API calls
export const fetchTasks = createAsyncThunk("dashboard/fetchTasks", async () => {
  try {
    const response = await getTasks();
    return response.data.tasks;
  } catch (error) {
    console.error("API call failed. Using dummy data for tasks.");
    // Dummy data for tasks
    return [
      {
        id: 1,
        title: "Task 1",
        description: "Complete the first task",
        start_date: "2024-12-01",
        due_date: "2024-12-05",
        priority: "High",
        tags: ["SOP"],
        task_status: "pending",
        related_to: 1,
        assigned_by: 101,
      },
      {
        id: 2,
        title: "Task 2",
        description: "Complete the second task",
        start_date: "2024-12-02",
        due_date: "2024-12-06",
        priority: "Medium",
        tags: ["Report"],
        task_status: "completed",
        related_to: 2,
        assigned_by: 102,
      },
    ];
  }
});
export const fetchStudents = createAsyncThunk(
  "dashboard/fetchStudents",
  async () => {
    try {
      const response = await getStudents();
      return response.data.data; // Return the entire data array
    } catch (error) {
      console.error("API call failed. Using dummy data for students.");
      // Dummy data for students
      return [
        {
          student_id: 48,
          first_name: "Komal",
          last_name: "Dokel",
          username: "komal dokel",
          email: "Komal.dokel@example.com",
          profile_picture_url: null,
          date_of_birth: null,
          city: null,
          resume_cv_url:
            "['https://aara-crm.s3.ap-south-1.amazonaws.com/resume_cv/sachin kaythamwar_7_12_2024 (10).pdf']",
          sop_url:
            "https://aara-crm.s3.ap-south-1.amazonaws.com/sop/Inhouse SWOT report.docx",
          lor_url: [
            "https://aara-crm.s3.ap-south-1.amazonaws.com/lor/sachin kaythamwar_9_12_2024 (1) (2).pdf",
            "https://aara-crm.s3.ap-south-1.amazonaws.com/lor/sachin kaythamwar_9_12_2024 (2) (1).pdf",
          ],
          university_sop_url:
            "https://aara-crm.s3.ap-south-1.amazonaws.com/uni_sop/sachin kaythamwar_9_12_2024 (1).pdf",
          university_essay_url:
            "https://aara-crm.s3.ap-south-1.amazonaws.com/uni_essay/sachin kaythamwar_9_12_2024 (1).pdf",
          final_doc_urls: {
            "Document 3": [
              "https://aara-crm.s3.ap-south-1.amazonaws.com/final_doc/sachin kaythamwar_9_12_2024 (2).pdf",
              "https://aara-crm.s3.ap-south-1.amazonaws.com/final_doc/sachin kaythamwar_9_12_2024 (1).pdf",
              "https://aara-crm.s3.ap-south-1.amazonaws.com/final_doc/sachin kaythamwar_9_12_2024.pdf",
            ],
          },
          shortlisting: null,
          status: "application_pending",
        },
        {
          student_id: 49,
          first_name: "Joyti",
          last_name: "Chaudhari",
          username: "joyti chaudhari",
          email: "joytichaudhari990@gmail.com",
          profile_picture_url: null,
          date_of_birth: null,
          city: null,
          resume_cv_url: null,
          sop_url: null,
          lor_url: null,
          university_sop_url: null,
          university_essay_url: null,
          final_doc_urls: null,
          shortlisting: null,
          status: "application_pending",
        },
        {
          student_id: 56,
          first_name: "Siya",
          last_name: "Shah",
          username: "siya shah",
          email: "siya@gmail.com",
          profile_picture_url: null,
          date_of_birth: null,
          city: null,
          resume_cv_url: null,
          sop_url: null,
          lor_url: null,
          university_sop_url: null,
          university_essay_url: null,
          final_doc_urls: null,
          shortlisting: null,
          status: "application_pending",
        },
      ];
    }
  }
);

export const addNewTask = createAsyncThunk(
  "dashboard/addNewTask",
  async (newTask) => {
    try {
      const response = await createTask(newTask);
      return response.data;
    } catch (error) {
      console.error("API call failed. Adding task locally.");
      // Return the task directly for local use
      return {
        id: Date.now(),
        ...newTask,
      };
    }
  }
);

// Initial State
const initialState = {
  tasks: [], // Will be populated with dummy data or API response
  students: [], // Will be populated with dummy data or API response
  selectedTask: null,
  selectedReminder: null,
  isLoading: false,
  error: null,
};

// Slice Definition
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    // Reducer to handle custom actions (if needed)
    resetError: (state) => {
      state.error = null;
    },
    setSelectedTask: (state, action) => {
      state.selectedTask = action.payload; // Set the selected task
    },
    resetSelectedTask: (state, action) => {
      state.selectedTask = null;
    },
    resetSelectedReminder: (state, action) => {
      state.selectedReminder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Tasks
      .addCase(fetchTasks.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // Fetch Students
      .addCase(fetchStudents.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.students = action.payload;
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // Add New Task
      .addCase(addNewTask.fulfilled, (state, action) => {
        state.tasks.push(action.payload);
      })
      .addCase(addNewTask.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

// Export Actions
export const {
  resetError,
  setSelectedTask,
  resetSelectedTask,
  resetSelectedReminder,
} = dashboardSlice.actions;

// Export Reducer
export default dashboardSlice.reducer;
